import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { loadVideoScene, loadSceneImage, saveGame } from "../../redux/asyncThunk/userActions";
import { userSlice } from "../../redux/user/userSlice";
import "reactflow/dist/style.css";
import "../index.css";
import { sanitizePlainText } from "../../utils/inputValidation";
import {
  getButtonStyle,
  getButtonPosition,
  getGroupStyle,
  getSceneStyle,
  SCHEME_SCENE,
  stripSchemeTransient,
  getNextTrainerButtonSlot,
  getTrainerGroupStyle,
} from "./schemeLayout";
import SchemeSceneContent from "./SchemeSceneContent";
import { useSceneScreenshot } from "./useSceneScreenshot";
import { getMaxButtonsForGame, isTrainerGame } from "../../utils/contentType";
import {
  buildQuestionModePatch,
  buildScenarioModePatch,
  inferScenarioContentMode,
  normalizeContentMode,
} from "../../utils/contentMode";

const { addMessage } = userSlice;

export const firstSceneNodes = [
  {
    id: "1-group",
    type: "group",
    className: "scheme-group",
    data: { label: "Стартовая сцена" },
    dragHandle: ".scheme-group-drag-handle",
    position: { x: 0, y: 0 },
    style: getGroupStyle(),
    deletable: false,
  },
  {
    id: "1",
    position: { x: SCHEME_SCENE.x, y: SCHEME_SCENE.y },
    data: {
      nodeType: "menu",
      video: "",
      hasVideo: false,
      videopath: "",
      screenpath: ``,
      sceneMode: "video",
      sceneText: "",
      hasImage: false,
      imagepath: "",
      questionMode: "video",
      questionText: "",
    },
    type: "first-scene-node",
    className: "scheme-scene",
    style: getSceneStyle(),
    extent: "parent",
    parentNode: "1-group",
    parentId: "1-group",
    draggable: false,
    deletable: false,
  },
];

export function buildInitialSceneNodes(isTrainer = false) {
  if (!isTrainer) return firstSceneNodes;

  return [
    {
      ...firstSceneNodes[0],
      data: { label: "Вопрос 1" },
      style: getTrainerGroupStyle(),
    },
    {
      ...firstSceneNodes[1],
      data: {
        ...firstSceneNodes[1].data,
        questionMode: "video",
        questionText: "",
      },
    },
  ];
}

/** @deprecated use buildInitialSceneNodes */
export function getFirstSceneNodes(isTrainer = false) {
  return buildInitialSceneNodes(isTrainer);
}

export function FirstSceneNode(props) {
  const { data, id } = props;
  const [buttonText, setButtonText] = useState("");
  const { hasScreenshot, hasImagePreview, imageSrc, imagePreviewSrc, imageKey, refreshScreenshot } =
    useSceneScreenshot(id, data);

  const selectedGameId = useSelector((state) => state.user?.selectedGameId);
  const game = useSelector((state) => state.user?.game);
  const user = useSelector((state) => state.user?.data?.user);
  const isTrainer = isTrainerGame(game);
  const maxButtons = getMaxButtonsForGame(game);

  const dispatch = useDispatch();

  const buttonNodes = (game?.nodes ?? []).filter(
    (el) =>
      el.type === "input" &&
      (el.parentId === "1-group" || el.parentNode === "1-group")
  );

  const persistSceneData = (patch) => {
    const updatedNodes = (game?.nodes ?? []).map((node) =>
      node.id === id
        ? { ...node, data: { ...node.data, ...patch } }
        : node
    );

    dispatch(
      saveGame({
        selectedGameId,
        nodes: stripSchemeTransient(updatedNodes),
        edges: game?.edges ?? [],
      })
    );
  };

  const handleVideoChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    dispatch(
      loadVideoScene({
        userId: user.id,
        selectedGameId,
        scene: String(id),
        file,
        game,
      })
    ).then(() => {
      refreshScreenshot();
    });
  };

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    dispatch(
      loadSceneImage({
        userId: user.id,
        selectedGameId,
        scene: String(id),
        file,
        game,
      })
    ).then(() => {
      refreshScreenshot();
    });
  };

  const sceneContentMode = isTrainer
    ? normalizeContentMode(data?.questionMode, "text")
    : inferScenarioContentMode(data);
  const sceneContentText = isTrainer
    ? data?.questionText ?? ""
    : data?.sceneText ?? data?.questionText ?? "";

  const handleContentModeChange = (mode) => {
    const patch = isTrainer ? buildQuestionModePatch(mode) : buildScenarioModePatch(mode);
    persistSceneData(patch);
  };

  const handleAddButtonNode = () => {
    if (!buttonText) {
      return dispatch(
        addMessage({
          type: "error",
          message: "Кнопка не может быть без текста",
        })
      );
    }

    if (buttonNodes.length >= maxButtons) {
      return dispatch(
        addMessage({
          type: "error",
          message: isTrainer
            ? "Можно добавить не более четырёх вариантов ответа"
            : "Сейчас вы можете добавить только две кнопки",
        })
      );
    }

    const slot = isTrainer
      ? getNextTrainerButtonSlot(buttonNodes)
      : buttonNodes.length && buttonNodes[0].data.buttonPosiiton === "top"
        ? "bottom"
        : "top";

    if (!slot) return;

    const checkButtonsInNode = buttonNodes[0];
    const newId = !checkButtonsInNode
      ? "1-1"
      : `1-${Number(checkButtonsInNode.id.split("-")[1]) + 1}`;

    const newButtonNode = {
      id: newId,
      data: {
        label: buttonText,
        buttonPosiiton: slot,
        isCorrect: false,
        explanation: "",
      },
      position: getButtonPosition(slot),
      className: "scheme-button",
      style: getButtonStyle(),
      parentId: "1-group",
      sourcePosition: "right",
      type: "input",
      extent: "parent",
      parentNode: "1-group",
      draggable: false,
    };

    setButtonText("");
    dispatch(
      saveGame({
        selectedGameId,
        nodes: stripSchemeTransient([...(game?.nodes ?? []), newButtonNode]),
        edges: game?.edges ?? [],
      })
    );
  };

  return (
    <SchemeSceneContent
      parentGroupId="1-group"
      hasScreenshot={hasScreenshot}
      imageKey={imageKey}
      imageSrc={imageSrc}
      hasImagePreview={hasImagePreview}
      imagePreviewSrc={imagePreviewSrc}
      buttonCount={buttonNodes.length}
      maxButtons={maxButtons}
      isTrainer={isTrainer}
      contentMode={sceneContentMode}
      contentText={sceneContentText}
      textPanelTitle={isTrainer ? "Текст вопроса" : "Текст сцены"}
      textPlaceholder={
        isTrainer ? "Введите вопрос для тренажёра" : "Введите текст сцены"
      }
      onContentModeChange={handleContentModeChange}
      onContentTextChange={(value) => {
        if (isTrainer) {
          persistSceneData({ questionText: sanitizePlainText(value, 1000) });
          return;
        }
        persistSceneData({ sceneText: sanitizePlainText(value, 2000) });
      }}
      onVideoSelected={handleVideoChange}
      onImageSelected={handleImageChange}
      onAddButton={handleAddButtonNode}
      buttonText={buttonText}
      onButtonTextChange={(value) =>
        setButtonText(sanitizePlainText(value, 100))
      }
    />
  );
}
